import { Component, OnInit, Input } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import { MonitorsSlaInflowsMonitorService } from 'e5core-angular-services';
import { MonitorsSlaInflowsMonitorModel } from 'e5core-angular-services';
import { DatePipe } from '@angular/common';
import { MatSelectChange } from '@angular/material';
import { Observable, of } from 'rxjs';

export interface OptionMonth {
  value: DateRange;
  viewValue: string;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export class MockInflowsService {
  public getMockedSlaInflowCounts(): Observable<MonitorsSlaInflowsMonitorModel> {
    const incomingData: any = new Array();
    const model: MonitorsSlaInflowsMonitorModel = new MonitorsSlaInflowsMonitorModel();
    incomingData.push({
      creationDate: new Date('2016-12-05T11:52:10.113Z'),
      activeWorkItemCount: 5,
      completeWorkItemCount: 4,
      diarisedWorkItemCount: 2,
      launchWorkItemCount: 3,
      rejectedWorkItemCount: 1,
      qaWorkItemCount: 2
    }, {
      creationDate: new Date('2016-12-06T11:52:10.113Z'),
      activeWorkItemCount: 8,
      completeWorkItemCount: 6,
      diarisedWorkItemCount: 4,
      launchWorkItemCount: 5,
      rejectedWorkItemCount: 3,
      qaWorkItemCount: 4
    }, {
      creationDate: new Date('2016-12-07T11:52:10.113Z'),
      activeWorkItemCount: 7,
      completeWorkItemCount: 5,
      diarisedWorkItemCount: 3,
      launchWorkItemCount: 4,
      rejectedWorkItemCount: 2,
      qaWorkItemCount: 3
    });
    model.data = incomingData;
    return of(model);
  }
}

@Component({
  selector: 'e5core-ui-monitor-sla-inflows-highcharts',
  templateUrl: './e5core-ui-monitor-sla-inflows-highcharts.component.html',
  styleUrls: ['./e5core-ui-monitor-sla-inflows-highcharts.component.css']
})
export class E5coreUiMonitorSlaInflowsHighchartsComponent implements OnInit {

  constructor(private monitorService: MonitorsSlaInflowsMonitorService, private datePipe: DatePipe) {  }

  @Input() slaRangeInDays = 28; // Idea is to display completed work items every day over a period of time. 28 days forms exactly a month
                                // which is a good range to display over.
  @Input() categorisation: number[];

  model: MonitorsSlaInflowsMonitorModel;
  startDate = '2018-01-11T13:21:53.770Z';
  endDate = '2018-30-11T13:35:36.870Z';
  Highcharts = Highcharts;
  chartConstructor = 'chart';
  chartOptions = this.createChartOptions();
  chartCallback = null; // optional function, defaults to null
  updateFlag = false; // optional boolean
  oneToOneFlag = true; // optional boolean, defaults to false
  runOutsideAngularFlag = false;

  // Ready up Mock Service
  mockInflowsService: MockInflowsService = new MockInflowsService();

  // Let the component know if it is dealing with mocked data
  isMocked: boolean;

  // Month drop-down variables
  currentDate: Date = new Date();
  optionMonths: OptionMonth[];
  currentDateRange: DateRange = {
    startDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1),
    endDate: new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0)
  };
  selectedOptionMonth: OptionMonth;
  selectedMonthViewValue: string;

  ngOnInit() {
    this.isMocked = true; // Set to false in production
    this.LoadChartObject(this.startDate, this.endDate);
    this.selectedOptionMonth = {
      value: this.currentDateRange,
      viewValue: this.datePipe.transform(this.currentDateRange.startDate, 'MMMM yyyy')
    };
    this.selectedMonthViewValue = this.selectedOptionMonth.viewValue;
    this.instantiateOptionMonths();
  }

  private addMonths(date: Date, months: number) {
    const newDate = date;
    newDate.setMonth(newDate.getMonth() + months);
    return (newDate);
  }

  private addToOptionMonths(startDate: Date, endDate: Date, negativeDirection: boolean) {
    const dates: Date[] = [];
    let currentDate = new Date(startDate);
    if (!negativeDirection) {
      while (currentDate <= endDate) {
        dates.push(currentDate);
        this.optionMonths.push({
          value: {
            startDate: new Date(currentDate),
            endDate: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0)
          },
          viewValue: this.datePipe.transform(currentDate, 'MMMM yyyy')
        });
        currentDate = this.addMonths(currentDate, 1);
      }
    }
  }

  private convertToMonthNumber(monthName: string) {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December'
    ];
    return months.indexOf(monthName);
  }

  private alterSelectedMonthOption(viewValue: string) {
    const selectedValue = this.optionMonths.filter(f => f.viewValue === viewValue);

    if (selectedValue.length === 0) {
      return;
    }

    this.selectedOptionMonth = {
      value: {
        startDate: selectedValue[0].value.startDate,
        endDate: selectedValue[0].value.endDate
      },
      viewValue: viewValue
    };
  }

  public reloadChart($event: MatSelectChange) {
    this.alterSelectedMonthOption(this.selectedMonthViewValue);
    const selectedDateRange = this.selectedOptionMonth.value;
    this.instantiateOptionMonths();
    this.LoadChartObject(selectedDateRange.startDate, selectedDateRange.endDate);
  }

  private instantiateOptionMonths() {
    this.optionMonths = [];

    const currentlySelectedDate = this.selectedOptionMonth.value.startDate;
    const startDate = new Date(currentlySelectedDate);
    const endDate = new Date(currentlySelectedDate);
    startDate.setMonth(startDate.getMonth() - 5);
    endDate.setMonth(endDate.getMonth() + 5);
    const today = new Date();
    if (endDate > today) {
      endDate.setMonth(today.getMonth());
    }
    this.addToOptionMonths(startDate, endDate, false);
  }

  public LoadChartObject(startDate: any, endDate: any, siteGroup ?: any, category1Id ?: any, category2Id ?: any,
    category3Id ?: any, archive ?: any, totalProperty ?: any) {
      if (this.isMocked) {
        this.mockInflowsService.getMockedSlaInflowCounts().subscribe(
          model => {
            this.chartOptions = {
              ...this.chartOptions,
              xAxis: this.extractXAxis(model),
              series: this.extractSeries(model)
            };
            this.model = model;
          },
        );
      } else {
        //TODO
        // this.monitorService.getMockedSlaInflowCounts().subscribe(
        //   model => {
        //     this.chartOptions = {
        //       ...this.chartOptions,
        //       xAxis: this.extractXAxis(model),
        //       series: this.extractSeries(model)
        //     };
        //     this.model = model;
        //   },
        // );
      }
  }


  private extractXAxis(model: MonitorsSlaInflowsMonitorModel): any {
    // debugger;
    const output = {
      type: 'datetime',
      ordinal: false,
      mintickInterval: 24 * 3600 * 1000,
      // categories: model.data.map(i => this.datePipe.transform(i.creationDate, 'dd MMM yyyy')),
      title: { rotation: -45 },
      labels: {
       format: '<b class="xAxisLabel">{value: %e %b %y}</b>'
      }
    };
    return (output);
  }

  private extractSeries(model: MonitorsSlaInflowsMonitorModel): any {
    // debugger;
    const series = [];
    // const completedWorkItems = [];
    const activeItems = [];
    const completedItems = [];
    const diarisedItems = [];
    const launchedItems = [];
    const rejectedItems = [];
    const qaItems = [];
     for (let i = 0; i < model.data.length; i++) {
       //TODO
    //   activeItems.push([model.data[i].creationDate.getTime(), model.data[i].activeWorkItemCount]);
    //   completedItems.push([model.data[i].creationDate.getTime(), model.data[i].completeWorkItemCount]);
    //   diarisedItems.push([model.data[i].creationDate.getTime(), model.data[i].diarisedWorkItemCount]);
    //   launchedItems.push([model.data[i].creationDate.getTime(), model.data[i].launchWorkItemCount]);
    //   rejectedItems.push([model.data[i].creationDate.getTime(), model.data[i].rejectedWorkItemCount]);
    //   qaItems.push([model.data[i].creationDate.getTime(), model.data[i].qaWorkItemCount]);
    }
    series.push({ name: 'Active', color: '#add8e6', data: activeItems, drilldown: true, yAxis: 0 });
    series.push({ name: 'Completed', color: '#228b22', data: completedItems, drilldown: true, yAxis: 0 });
    series.push({ name: 'Diarised', color: '#a52a2a', data: diarisedItems, drilldown: true, yAxis: 0});
    series.push({ name: 'Launched', color: '#ffff00', data: launchedItems, drilldown: true, yAxis: 0});
    series.push({ name: 'Rejected', color: '#ff0000', data: rejectedItems, drilldown: true, yAxis: 0});
    series.push({ name: 'QA', color: '#ffa500', data: qaItems, drilldown: true, yAxis: 0});
    return (series);
  }

  private drillDown(event) {
    alert(event) ;
  }

  createChartOptions() {
    const _this: E5coreUiMonitorSlaInflowsHighchartsComponent = this;
    return {
      chart: {
        type: 'column',
        events: {
          drilldown: function (event) {
              _this.drillDown(event);
          }
        }
      },
      title: {
        text: 'Work SLA Inflows Monitor',
        style: {
          'fontSize': '16px',
          'font-weight': 'bold'
        }
      },
      xAxis: {
        categories: [],
        title: { rotation : -45}
      },
      yAxis: [{
        min: 0,
        title: {
          text: 'Work Item Count',
        },
        labels: {x: -15},
        stackLabels: {
          enabled: true,
          style: {
            fontWeight: 'bold',
            color: 'gray'
          }
        }
      }],
      legend: {
        align: 'right',
        x: -30,
        verticalAlign: 'top',
        y: 25,
        floating: true,
        backgroundColor: 'white',
        borderColor: '#CCC',
        borderWidth: 1,
        shadow: false,
        itemStyle: {
          fontSize: '12px',
          font: '12pt',
        }
      },
      tooltip: {
        headerFormat: '<b>{point.x: %e %b %y}</b><br/>',
        pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
      },
      plotOptions: {
      series: {
          stacking: 'normal',
          dataLabels: {
            enabled: true,
            align: 'center',
            color: '#FFFFFF'
          },
        }
      },
      series: [
        {
          name: 'Work Items Completed',
          color: '#00cc99',
          data: []
        }
      ],
      drilldown: {
        series: []
      }
    };
  }

  injectMockInformation(startDate: string, endDate: string) {

  }

  onNavigationCategoryClick(categoryName) {

  }

}



